import {
  Graphics,
  Container,
  ContainerChild,
  FillGradient,
  Text,
  CanvasTextMetrics,
} from "pixi.js";

import type { IStudioElement } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Elements.interface";
import type { IOverlayFill } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

import { TimerService } from "../Services/TimerService";

import {
  anchorXForAlign,
  ALIGNMENT_OFFSET,
  buildLinearGradientLocalVectors,
  decorationThickness,
  resolveTextStyle,
  underlineOffsetPx,
  wordWrapWidth,
  type TextStyle,
} from "../../../Text/textLayout";

/**
 * Build a Pixi v8 FillGradient using the shared linear/radial conventions
 * from `textLayout.ts`. The CSS angle convention (0deg → up, 90deg → right)
 * and local texture space (0..1) are computed once for both Pixi and the
 * Assembler's canvas2D renderer — eliminating subtle gradient drift between
 * the editor preview and the broadcast.
 */
const buildTextFill = (
  fontGradient: IOverlayFill | undefined,
  fontColor: string,
  width: number,
  height: number,
): string | FillGradient => {
  if (!fontGradient || fontGradient.kind === "solid") {
    return fontGradient?.kind === "solid" ? fontGradient.color : fontColor;
  }

  const colorStops = fontGradient.stops.map((s) => ({
    offset: s.position / 100,
    color: s.color,
  }));
  const textureSize = Math.max(
    256,
    Math.min(2048, Math.round(Math.max(width, height))),
  );

  try {
    if (fontGradient.kind === "linear") {
      const { start, end } = buildLinearGradientLocalVectors(
        fontGradient.angle ?? 0,
        width,
        height,
      );
      return new FillGradient({
        type: "linear",
        start,
        end,
        colorStops,
        textureSpace: "local",
        textureSize,
      });
    }
    return new FillGradient({
      type: "radial",
      center: { x: 0.5, y: 0.5 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 1,
      colorStops,
      textureSpace: "local",
      textureSize,
    });
  } catch {
    return fontColor;
  }
};

export class TextElementRenderer {
  private textObjects = new Map<string, Text>();

  private updateCallbacks = new Map<string, (time: number) => void>();

  render(
    element: IStudioElement,

    container: Container,

    destination: { x: number; y: number; width: number; height: number },
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options = (element as any).options || {};

    const style: TextStyle = resolveTextStyle(options);
    const textContent: string = options.text ?? "";
    const isSyncTimer: boolean = options.syncTimer === true;

    if (style.backgroundColor) {
      const bg = new Graphics();

      bg.rect(0, 0, destination.width, destination.height).fill(
        style.backgroundColor,
      );

      container.addChild(bg);
    }

    const fillStyle = buildTextFill(
      style.fontGradient,
      style.fontColor,
      destination.width,
      destination.height,
    );

    const text = new Text({
      text: textContent,

      style: {
        fontFamily: style.fontFamily,

        fontSize: style.fontSize,

        fill: fillStyle,

        align: style.textAlign,

        fontWeight: style.bold ? "bold" : "normal",

        fontStyle: style.italic ? "italic" : "normal",

        wordWrap: true,

        wordWrapWidth: wordWrapWidth(destination.width),

        lineHeight: style.lineHeightPx,

        letterSpacing: style.letterSpacingPx,
      },
    });

    text.anchor.y = 0.5;
    text.y = destination.height / 2;
    text.anchor.x = anchorXForAlign(style.textAlign);

    // Horizontal Alignment — center uses width/2, left/right keep the
    // lateral gutter so glyphs don't hug the bounding edge.
    if (style.textAlign === "center") {
      text.x = destination.width / 2;
    } else if (style.textAlign === "right") {
      text.x = destination.width - ALIGNMENT_OFFSET;
    } else {
      text.x = ALIGNMENT_OFFSET;
    }

    container.addChild(text as unknown as ContainerChild);

    // Decorations (Underline / Strike)

    if (style.underline || style.strike) {
      const metrics = CanvasTextMetrics.measureText(textContent, text.style);

      const graphics = new Graphics();

      const lineHeight = style.lineHeightPx;
      const lines = metrics.lines || [textContent];
      const lineWidths = metrics.lineWidths || lines.map(() => 0);

      const lineThickness = decorationThickness(style.fontSize);
      const underlineOffset = underlineOffsetPx(style.fontSize);
      const strikeOffset = 0;

      let currentY = -((lines.length * lineHeight) / 2);

      lines.forEach((_: string, i: number) => {
        const lineWidth = lineWidths[i] || 0;

        let startX = 0;
        if (style.textAlign === "center") {
          startX = -lineWidth / 2;
        } else if (style.textAlign === "right") {
          startX = -lineWidth;
        }

        if (style.underline) {
          graphics.moveTo(startX, currentY + lineHeight / 2 + underlineOffset);
          graphics.lineTo(
            startX + lineWidth,
            currentY + lineHeight / 2 + underlineOffset,
          );
          graphics.stroke({ width: lineThickness, color: style.fontColor });
        }

        if (style.strike) {
          graphics.moveTo(startX, currentY + lineHeight / 2 + strikeOffset);
          graphics.lineTo(
            startX + lineWidth,
            currentY + lineHeight / 2 + strikeOffset,
          );
          graphics.stroke({ width: lineThickness, color: style.fontColor });
        }

        currentY += lineHeight;
      });

      graphics.x = text.x;
      graphics.y = text.y;

      container.addChild(graphics);
    }

    const elementKey = `text_${element.id}`;

    this.textObjects.set(elementKey, text);

    if (isSyncTimer) {
      const timerService = TimerService.getInstance();

      if (this.updateCallbacks.has(elementKey)) {
        timerService.unsubscribe(this.updateCallbacks.get(elementKey)!);
      }

      const updateCallback = (now: number) => {
        const startTime = timerService.getStartTime();

        const value = this.calculateTimer(startTime, now);

        if (text.text !== value) {
          text.text = value;
        }
      };

      this.updateCallbacks.set(elementKey, updateCallback);

      timerService.subscribe(updateCallback);
    }
  }

  private calculateTimer(startTime: number, now: number): string {
    const elapsed = now - startTime;

    const totalSeconds = Math.floor(elapsed / 1000);

    const hours = Math.floor(totalSeconds / 3600);

    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const seconds = totalSeconds % 60;

    const pad = (num: number) => String(num).padStart(2, "0");

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  public destroy() {
    const timerService = TimerService.getInstance();

    for (const [_key, callback] of this.updateCallbacks.entries()) {
      timerService.unsubscribe(callback);
    }

    this.updateCallbacks.clear();

    this.textObjects.clear();
  }
}
